import { Injectable } from '@angular/core';
import { HttpService } from './http.service';
import { Router } from '@angular/router';
import { NativeStorageService } from './native-storage.service';
import { User } from './types/user.interface';
import { Company } from './types/company.interface';
import { ApiResponse, LoginResponse } from './types/api-responses.interface';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public token: string;
  public user: User;
  public company: Company | null = null;
  public loginFirst: boolean = false;

  public forecast = 0;
  public manageView = false;

  // VP-4: Company-Daten (Deferred Loading — erst nach erster Adresssuche verfuegbar)
  get companyLogo(): string | null {
    return this.company?.logo || null;
  }

  get companyName(): string | null {
    return this.company?.name || null;
  }

  // VP-4-v2: ColorCode aus Company-Daten (fuer dynamische Primary-Color)
  get companyColorCode(): string | null {
    return this.company?.colorCode || null;
  }

  // VP-Category-Icons: Category-Icon aus Company-Daten holen
  getCategoryIcon(categoryType: string): string | null {
    if (!this.company?.icons) return null;
    const key = categoryType.toLowerCase() as keyof typeof this.company.icons;
    return this.company.icons[key] || null;
  }

  constructor(
    private http: HttpService,
    private router: Router,
    private nativeStorage: NativeStorageService,
  ) {}

  // VP-4: Company-Branding setzen (temporaer — nur waehrend Verkaufsprozess)
  setCompany(company: Company): void {
    if (!company) return;
    // B-7 (Story 5.3): idempotent guard. When colorCode AND logo are unchanged,
    // refresh the reference for downstream getters but skip the 5 CSS-custom-property
    // writes — they trigger an app-wide restyling cascade that lands in the modal
    // animation frame from selectRate.
    if (
      this.company &&
      this.company.colorCode === company.colorCode &&
      this.company.logo === company.logo
    ) {
      this.company = company;
      return;
    }
    this.company = company;
    // NICHT in NativeStorage persistieren — Branding ist nur fuer den aktiven Verkaufsprozess
    const defaultPrimary = '#009FCC';
    const hex = company.colorCode || defaultPrimary;
    document.documentElement.style.setProperty('--ion-color-primary', hex);
    document.documentElement.style.setProperty('--ion-color-primary-rgb', this.hexToRgb(hex));
    if (hex === defaultPrimary) {
      document.documentElement.style.setProperty('--ion-color-primary-shade', '#ffffff');
      document.documentElement.style.setProperty('--ion-color-primary-tint', '#343433');
    } else {
      document.documentElement.style.setProperty('--ion-color-primary-shade', this.shadeColor(hex, -12));
      document.documentElement.style.setProperty('--ion-color-primary-tint', this.shadeColor(hex, 10));
    }
  }

  // VP-4: Company-Branding zuruecksetzen (nach Verkaufsprozess / zurueck zum Availability-Check)
  clearCompany(): void {
    this.company = null;
    this.nativeStorage.storeOffline('company', null).then(
      () => {},
      (_err) => {},
    );
    const defaultPrimary = '#009FCC';
    document.documentElement.style.setProperty('--ion-color-primary', defaultPrimary);
    document.documentElement.style.setProperty('--ion-color-primary-rgb', '0, 159, 204');
    document.documentElement.style.setProperty('--ion-color-primary-shade', '#ffffff');
    document.documentElement.style.setProperty('--ion-color-primary-tint', '#343433');
  }

  // VP-4-v2: Company-Daten aus NativeStorage wiederherstellen (Offline-Verfuegbarkeit)
  loadCompany(): Promise<void> {
    return this.nativeStorage.getOffline('company').then(
      (data: Company) => {
        this.company = data;
        // VP-4-v2: Primary-Color nach Restore wiederherstellen (inkl. RGB, Shade, Tint — R-1 Fix)
        if (data && data.colorCode) {
          const hex = data.colorCode;
          document.documentElement.style.setProperty('--ion-color-primary', hex);
          document.documentElement.style.setProperty('--ion-color-primary-rgb', this.hexToRgb(hex));
          document.documentElement.style.setProperty('--ion-color-primary-shade', this.shadeColor(hex, -12));
          document.documentElement.style.setProperty('--ion-color-primary-tint', this.shadeColor(hex, 10));
        }
      },
      () => {
        this.company = null;
      },
    );
  }

  calculcateForecast(): void {
    const now = new Date();
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    this.forecast = Math.round(((this.user.workParameters.sales.month || 0) / now.getDate()) * lastDay.getDate());
  }

  refresh(): void {
    this.http.get<ApiResponse<User>>('/auth/users/me').then(
      (response: ApiResponse<User>) => {
        this.user = response.message;
      },
      (_err) => {},
    );
  }

  register(details: Record<string, unknown>): Promise<LoginResponse> {
    return this.http.post<LoginResponse>('/auth/register', details).then((response: LoginResponse) => {
      this.token = response.token;
      return response;
    });
  }

  login(credentials: { mid: string; password: string }): Promise<LoginResponse> {
    const path = credentials.mid.includes('admin') ? '/auth/login/adm' : '/auth/login';
    return this.http.post<LoginResponse>(path, credentials).then((response: LoginResponse) => {
      this.token = response.token;
      return response;
    });
  }

  sms(details: Record<string, unknown>): Promise<unknown> {
    return this.http.post<unknown>('/auth/sms', details);
  }

  reset(credentials: Record<string, unknown>): Promise<LoginResponse> {
    return this.http.post<LoginResponse>('/auth/changePassword', credentials).then((response: LoginResponse) => {
      this.token = response.token;
      return response;
    });
  }

  switchAdmin(): void {
    if (!this.manageView) {
      this.manageView = true;
      this.router.navigateByUrl('tabs/5');
    } else {
      this.manageView = false;
      this.router.navigateByUrl('tabs/1');
    }
  }

  // VP-4-v2 R-1 Fix: Hex-Farbe zu komma-separiertem RGB-String konvertieren
  private hexToRgb(hex: string): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `${r}, ${g}, ${b}`;
  }

  // VP-4-v2 R-1 Fix: Hex-Farbe aufhellen (percent > 0) oder abdunkeln (percent < 0)
  private shadeColor(hex: string, percent: number): string {
    const r = Math.min(255, Math.max(0, parseInt(hex.slice(1, 3), 16) + Math.round(2.55 * percent)));
    const g = Math.min(255, Math.max(0, parseInt(hex.slice(3, 5), 16) + Math.round(2.55 * percent)));
    const b = Math.min(255, Math.max(0, parseInt(hex.slice(5, 7), 16) + Math.round(2.55 * percent)));
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }

  // DEPRECATED
  isVP(username: string | number): boolean {
    return /^[A-Za-z]+-\d+$/.test(String(username));
  }

  changePasswordVP(newPassword: string): Promise<unknown> {
    return this.http.post<unknown>('/auth/changePasswordVP', { newPassword });
  }
}
